/*---------------------------------------------------------------
 ?? Project: Ring2All Platform
 ????? Author: Rodrigo Cuadra
 ?? Company: Ring2All LLC
 ?? File: tabs\DeviceDssTab.tsx
 ??? Created: 2026-05-02
 ?? Description:
    Implementation of the Provisioning Devices module.
----------------------------------------------------------------*/

import { AlertCircle, RefreshCw, Edit, LayoutTemplate } from 'lucide-react';
import { FormBox } from '../../../components/ui/FormBox';
import Combobox from '../../../components/ui/Combobox';
import { useI18n } from '../../../hooks/useI18n';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { extractDssKeysInfo } from '../../provisioning-templates/utils/extractOptions';
import { DssKeyConfig } from '../../provisioning-templates/types';
import type { DeviceModel } from '../../provisioning-device-models/api';
import type { Template } from '../../provisioning-templates/api';

interface DeviceDssTabProps {
  selectedModel: DeviceModel;
  selectedTemplate: Template | null;
  deviceKeys: DssKeyConfig[];
  setDeviceKeys: React.Dispatch<React.SetStateAction<DssKeyConfig[]>>;
  isKeysOverridden: boolean;
  setIsKeysOverridden: (val: boolean) => void;
  dssPage: number;
  setDssPage: React.Dispatch<React.SetStateAction<number>>;
  dssPerPage: number;
  setDssPerPage: React.Dispatch<React.SetStateAction<number>>;
}

export function DeviceDssTab({
  selectedModel, selectedTemplate, deviceKeys, setDeviceKeys,
  isKeysOverridden, setIsKeysOverridden,
  dssPage, setDssPage, dssPerPage, setDssPerPage
}: DeviceDssTabProps) {
  const { t } = useI18n();
  const dssInfo = extractDssKeysInfo(selectedModel.keys, selectedModel.accounts);
  const totalItems = deviceKeys.length;
  const totalPages = Math.ceil(totalItems / dssPerPage);
  const startIndex = (dssPage - 1) * dssPerPage;
  const endIndex = Math.min(startIndex + dssPerPage, totalItems);
  const currentPageKeys = deviceKeys.slice(startIndex, endIndex);
  const lineOptions = Array.from({ length: dssInfo.maxAccounts }, (_, i) => ({ value: `Line ${i + 1}`, label: `Line ${i + 1}` }));

  const updateDssKey = (keyIndex: number, field: keyof DssKeyConfig, value: string) => {
    if (!isKeysOverridden) return;
    const absoluteIndex = startIndex + keyIndex;
    setDeviceKeys(prev => { const updated = [...prev]; updated[absoluteIndex] = { ...updated[absoluteIndex], [field]: value }; return updated; });
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg border border-blue-200 dark:border-blue-800">
        <div className="flex items-center gap-3">
          <AlertCircle className="h-5 w-5 text-blue-600 dark:text-blue-400" />
          <div>
            <h4 className="font-semibold text-blue-900 dark:text-blue-100">
              {isKeysOverridden ? t('provisioningDevices.customKeys', 'Custom Configuration') : t('provisioningDevices.templateKeys', 'Using Template Configuration')}
            </h4>
            <p className="text-sm text-blue-700 dark:text-blue-300">
              {isKeysOverridden ? t('provisioningDevices.customKeysDesc', 'You are overriding the template settings for this device.') : t('provisioningDevices.templateKeysDesc', 'This device is using settings defined in the selected template.')}
            </p>
          </div>
        </div>
        {isKeysOverridden ? (
          <button type="button" onClick={() => { setIsKeysOverridden(false); if (selectedTemplate?.keys) setDeviceKeys(selectedTemplate.keys as DssKeyConfig[]); }}
            className="flex items-center gap-2 px-3 py-2 bg-white dark:bg-black text-foreground border rounded-md shadow-sm hover:bg-gray-50">
            <RefreshCw className="h-4 w-4" />{t('provisioningDevices.resetToTemplate', 'Reset to Template')}
          </button>
        ) : (
          <button type="button" onClick={() => setIsKeysOverridden(true)}
            className="flex items-center gap-2 px-3 py-2 bg-blue-600 text-white rounded-md shadow-sm hover:bg-blue-700">
            <Edit className="h-4 w-4" />{t('provisioningDevices.customize', 'Customize Keys')}
          </button>
        )}
      </div>

      <FormBox title={t('provisioningDevices.keysConfig', 'Key Configuration')} icon={<LayoutTemplate className="h-4 w-4" />}>
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <select value={dssPerPage} onChange={(e) => { setDssPerPage(Number(e.target.value)); setDssPage(1); }} className="px-2 py-1 border border-border rounded text-sm bg-background">
              <option value={10}>10</option><option value={20}>20</option><option value={40}>40</option>
            </select>
            <span className="text-sm text-muted-foreground">{t('common.itemsPerPage', 'entries per page')}</span>
          </div>
        </div>
        <div className="border border-border rounded-lg overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-muted/50 border-b border-border">
              <tr>
                <th className="px-3 py-2 text-left font-medium w-16">{t('provisioningTemplates.key', 'Key')}</th>
                <th className="px-3 py-2 text-left font-medium w-60">{t('provisioningTemplates.type', 'Type')}</th>
                <th className="px-3 py-2 text-left font-medium">{t('common.description', 'Description')}</th>
                <th className="px-3 py-2 text-left font-medium">{t('provisioningTemplates.value', 'Value')}</th>
                <th className="px-3 py-2 text-left font-medium">{t('provisioningTemplates.extension', 'Extension')}</th>
                <th className="px-3 py-2 text-left font-medium w-40">{t('provisioningTemplates.line', 'Line')}</th>
              </tr>
            </thead>
            <tbody>
              {currentPageKeys.map((key, idx) => (
                <tr key={key.keyNumber} className="border-b border-border last:border-0 hover:bg-muted/30">
                  <td className="px-3 py-2 text-muted-foreground font-medium">{key.keyNumber}</td>
                  <td className="px-3 py-1">
                    {isKeysOverridden ? (
                      <Combobox single options={dssInfo.types.map((type: { value: string; label: string }) => ({ value: type.value, label: type.label, checked: key.type === type.value }))}
                        onToggle={(val) => updateDssKey(idx, 'type', val)} placeholder={t('provisioningTemplates.selectType', 'Select type...')} className="w-full" />
                    ) : <div className="px-3 py-2 text-sm text-muted-foreground">{dssInfo.types.find((type) => type.value === key.type)?.label || key.type}</div>}
                  </td>
                  <td className="px-3 py-1"><input type="text" value={key.description} onChange={(e) => updateDssKey(idx, 'description', e.target.value)} className="w-full px-2 py-1 border border-border rounded text-sm bg-background disabled:opacity-50" disabled={!isKeysOverridden} /></td>
                  <td className="px-3 py-1"><input type="text" value={key.value} onChange={(e) => updateDssKey(idx, 'value', e.target.value)} className="w-full px-2 py-1 border border-border rounded text-sm bg-background disabled:opacity-50" disabled={!isKeysOverridden} /></td>
                  <td className="px-3 py-1"><input type="text" value={key.extension} onChange={(e) => updateDssKey(idx, 'extension', e.target.value)} className="w-full px-2 py-1 border border-border rounded text-sm bg-background disabled:opacity-50" disabled={!isKeysOverridden} /></td>
                  <td className="px-3 py-1">
                    {isKeysOverridden ? (
                      <Combobox single options={lineOptions.map(opt => ({ value: opt.value, label: opt.label, checked: key.line === opt.value }))}
                        onToggle={(val) => updateDssKey(idx, 'line', val)} placeholder={t('provisioningTemplates.selectLine', 'Select line...')} className="w-full" />
                    ) : <div className="px-3 py-2 text-sm text-muted-foreground">{lineOptions.find(opt => opt.value === key.line)?.label || key.line}</div>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {/* Pagination */}
        <div className="flex items-center justify-between mt-4">
          <div className="text-sm text-muted-foreground">{t('common.showing', 'Showing')} {startIndex + 1} {t('common.to', 'to')} {endIndex} {t('common.of', 'of')} {totalItems} {t('common.results', 'entries')}</div>
          <div className="flex items-center gap-1">
            <button type="button" onClick={() => setDssPage(1)} disabled={dssPage === 1} className="px-2 py-1 text-sm border border-border rounded hover:bg-muted disabled:opacity-50 disabled:cursor-not-allowed">�</button>
            <button type="button" onClick={() => setDssPage(p => Math.max(1, p - 1))} disabled={dssPage === 1} className="px-2 py-1 text-sm border border-border rounded hover:bg-muted disabled:opacity-50 disabled:cursor-not-allowed"><ChevronLeft className="h-4 w-4" /></button>
            {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
              let pageNum = i + 1;
              if (totalPages > 5) { if (dssPage <= 3) pageNum = i + 1; else if (dssPage >= totalPages - 2) pageNum = totalPages - 4 + i; else pageNum = dssPage - 2 + i; }
              return (<button key={pageNum} type="button" onClick={() => setDssPage(pageNum)} className={`px-3 py-1 text-sm border border-border rounded ${dssPage === pageNum ? 'bg-primary text-primary-foreground' : 'hover:bg-muted'}`}>{pageNum}</button>);
            })}
            <button type="button" onClick={() => setDssPage(p => Math.min(totalPages, p + 1))} disabled={dssPage === totalPages} className="px-2 py-1 text-sm border border-border rounded hover:bg-muted disabled:opacity-50 disabled:cursor-not-allowed"><ChevronRight className="h-4 w-4" /></button>
            <button type="button" onClick={() => setDssPage(totalPages)} disabled={dssPage === totalPages} className="px-2 py-1 text-sm border border-border rounded hover:bg-muted disabled:opacity-50 disabled:cursor-not-allowed">�</button>
          </div>
        </div>
      </FormBox>
    </div>
  );
}
